/* eslint-disable @typescript-eslint/no-explicit-any */
import { TDataDisplayType } from "@dw/models/shared";
import { formatCurrency, formatNumber } from "./numberUtils";
import { formatDate } from "./dateUtils";
import { IDanceClass } from "@dw/models/danceClass";
import { DATE_FORMATS } from "@dw/constants";
import { Dayjs, dayjs } from '@dw/dayjs';
import { COUNTRY_CODE } from "@dw/constants/intl";
import { IStudent } from "@dw/models/dancerAccount";

export const formatQueryParamString = (params?: Record<string, any>): string => {
  if (!params) {
    return '';
  }

  const queryString = Object.keys(params)
    .filter(key => params[key] !== null && params[key] !== undefined && params[key] !== '')
    .map(key => {
      let paramString = params[key];
      if (typeof params[key] === 'object' && !Array.isArray(params[key])) {
        paramString = JSON.stringify(params[key]); // This will generally be Date objects, so stringify will convert to ISO date

        if (dayjs.isDayjs(params[key])) { // Check if this is a dayjs object
          paramString = params[key].toISOString();
        } else if (dayjs(params[key]).isValid()) { // Check if this is a Date objet
          paramString = dayjs(params[key]).toISOString();
        }
      } else if (Array.isArray(params[key])) {
        const paramStringParts: string[] = [];
        for (const item of params[key]) {
          paramStringParts.push(`${key}=${encodeURIComponent(item)}`);
        }
        return paramStringParts.join('&');
      }
      return `${key}=${encodeURIComponent(paramString)}`;
    })
    .join('&');

  return `?${queryString}`;
};

export const formatRoute = (route: string, routeParams: Record<string, string>, queryParams?: Record<string, string | undefined>): string => {
  let formattedRoute = `${route}`;

  Object.keys(routeParams).forEach((key) => {
    formattedRoute = formattedRoute.replace(`:${key}`, encodeURIComponent(routeParams[key]));
  });

  let queryParamsString = '';
  if (queryParams) {
    queryParamsString = formatQueryParamString(queryParams);
  }

  return `${formattedRoute}${queryParamsString}`;
}

export const camelCaseToUserText = (input: string): string => (input ?? '').split(/(?=[A-Z])/).map(x => `${x[0].toUpperCase()}${x.substring(1)}`).join(' ');
export const convertToKebabCase = (input: string): string => (
  splitByUpperCase(input).split(' ').join('-').toLowerCase()
);

export const splitByUpperCase = (input?: string): string => !input ? '' : (
  `${input.replace(/([A-Z](?=[a-z]+)|[A-Z]+(?![a-z]))/g, ' $1').slice(1)}`
);

export const cleanupPhoneNumber = (number?: string | null): string => {
  if (!number) {
    return '';
  }

  // eslint-disable-next-line no-useless-escape
  return number.replace(/[ \(\)\-]/g, '');
}

export const formatAsDisplayType = (value: any, displayType: TDataDisplayType | undefined, countryCode?: COUNTRY_CODE) => {
  if (displayType === 'currency') {
      return formatCurrency(value, countryCode);
  } else if (displayType === 'date' || displayType === 'datetime' || displayType === 'time') {
      return formatDate(value, displayType);
  } else if (typeof value === 'number' || displayType === 'number') {
      return formatNumber(value, countryCode);
  }

  return value;
}

export const formatTimeRange = (startDateTime?: Dayjs, endDateTime?: Dayjs): string => {
  if (!startDateTime || !endDateTime) {
    return '';
  }

  return `${startDateTime.format(DATE_FORMATS.HourMinute)} - ${endDateTime.format(DATE_FORMATS.HourMinute)}`
}

export const formatTimeRangeDuration = (startDateTime?: Dayjs, endDateTime?: Dayjs): string => {
  if (!startDateTime || !endDateTime) {
    return '';
  }

  const durationMinutesTotal = endDateTime.diff(startDateTime, 'minutes');
  const durationHours = Math.floor(durationMinutesTotal / 60);
  const durationMinutes = durationMinutesTotal - (durationHours * 60);

  const textParts: string[] = [];
  if (durationHours > 0) {
    textParts.push(`${durationHours} hour`);
  }
  if (durationMinutes > 0) {
    textParts.push(`${durationMinutes} min`);
  }

  return textParts.join(' ');
}

export const formatDanceClassTime = (danceClass?: Partial<IDanceClass>): string => {
  if (!danceClass?.startTime || !danceClass?.endTime) {
    return '';
  }
  return formatTimeRange(danceClass.startTime, danceClass.endTime);
}

export const formatDanceClassAgeString = (ageDetails: Partial<IDanceClass>): string => {
  if (!ageDetails.minAge) {
    return `${ageDetails.maxAge} and under`;
  } else if (!ageDetails.maxAge) {
    return `${ageDetails.minAge}+`;
  } else if (ageDetails.minAge === ageDetails.maxAge) {
    return ageDetails.minAge.toString();
  } else {
    return `${ageDetails.minAge} - ${ageDetails.maxAge}`;
  }
}

export const formatDanceClassName = (danceClass?: Partial<IDanceClass>): string => {
  if (!danceClass) {
    return '';
  } else if (danceClass.name) {
    return danceClass.name;
  }

  return [
    splitByUpperCase(danceClass.danceStyle),
    danceClass.minAge || danceClass.maxAge ? ` (${formatDanceClassAgeString(danceClass)})` : ''
  ].join('');
}

export const formatStudentFullName = (student?: IStudent): string => {
  if (!student) {
    return '';
  }

  return `${student.firstName} ${student.lastName}`;
}

export const formatStudentNameWithAge = (student?: IStudent): string => {
  if (!student) {
    return '';
  }

  const fullName = formatStudentFullName(student);
  const today = dayjs();
  const age = `(${today.diff(student.dateOfBirth, 'years', false)}y)`

  return `${fullName} ${age}`;
}

export const plurify = (singularText: string, value: number, pluralSuffix?: string): string => `${singularText}${value !== 1 ? (pluralSuffix ?? 's') : ''}`;

export const getRouteName = (route: string, parentRoute?: string): string => {
  let routeName = route;
  if (parentRoute) {
    routeName = routeName.replace(parentRoute, '');
  }
  if (routeName.startsWith('/')) {
    routeName = routeName.substring(1);
  }
  if (routeName.endsWith('/')) {
    routeName = routeName.substring(0, routeName.length - 1);
  }
  return routeName;
}

export const compareToRoutePattern = (routePattern: string, pathName: string): boolean => {
  const regexPattern = new RegExp("^" + routePattern.replace(/:[A-Za-z]+/g, "([^/]+)") + "$");
  return regexPattern.test(pathName);
}

export const copyToClipboard = (text: string, onSuccess?: () => void, onError?: () => void): void => {
  navigator.clipboard.writeText(text).then(() => onSuccess?.()).catch(() => onError?.());
}